import {
  KeywordEffect,
  KeywordPurchaseOption,
  KeywordRef,
  KeywordType,
  replaceKeywordValue,
} from "./keyword-types.js";
import { NumericalKeywordType } from "./numerical-keyword-type.js";
import { Trait, injectTraits, toTrait } from "./traits.js";

export type KeywordDefinitionData = {
  id: string;
  name: string;
  description: string;
  traits: string[];
  effects?: any[];
  purchase_options?: any[];
};

export class KeywordDefinition {
  id: string;
  description: string;
  type: KeywordType;
  traits: Trait[];
  effects: KeywordEffect[];
  purchaseOptions: KeywordPurchaseOption[];
  data: KeywordDefinitionData;
  ref?: KeywordRef;

  /**
   * Constructs a new Keyword Definition from another keyword definition and a ref.
   *
   * This is useful in places like cursed artefacts, where we have a list of keyword definitions,
   * but with concrete values (e.g. Swift [4]).
   */
  static fromRef(def: KeywordDefinition, ref: KeywordRef) {
    return new KeywordDefinition(def.data, ref);
  }

  constructor(data: KeywordDefinitionData, ref?: KeywordRef) {
    this.data = data;
    this.id = data.id;
    this.description = data.description;
    this.type = data.name.endsWith("[x]") ? KeywordType.NUMERICAL_KEYWORD : KeywordType.KEYWORD;
    this.traits = data.traits.map(toTrait);
    this.effects = (data.effects as KeywordEffect[]) ?? [];
    this.purchaseOptions = data.purchase_options ?? [];
    this.ref = ref;
  }

  get name() {
    const ref = this.ref;

    if (ref?.numerical) {
      const operator = ref.numerical.type === NumericalKeywordType.INCREMENT ? "+" : "";
      const value = `${operator}${ref.numerical.value}`;
      return replaceKeywordValue(this.data.name, value);
    }

    return this.data.name;
  }

  get descriptionWithTraits() {
    const ref = this.ref;

    if (ref?.numerical) {
      // We intentionally don't include the operator here. The keyword definition reads better
      // that way.
      return injectTraits(replaceKeywordValue(this.description, ref.numerical.value), this.traits);
    }

    return injectTraits(this.description, this.traits);
  }

  hasTrait(this: KeywordDefinition, trait: Trait) {
    return this.traits.some((t) => t === trait);
  }

  isNumerical(this: KeywordDefinition) {
    return this.type === KeywordType.NUMERICAL_KEYWORD;
  }

  isPurchaseable() {
    return this.purchaseOptions.filter((p) => p.cost !== 0).length > 0;
  }

  isDemoKeyword() {
    return [
      "devastating_charge",
      "distracted",
      "fragile",
      "immobile",
      "nimble",
      "skirmishers",
      "tough",
      "towering",
      "wild",
    ].includes(this.id);
  }
}
